// V4-FRONT-ANY-USAGE batch 5 2026-05-28
import { MessageCircle, AlignLeft, RefreshCw, Palette } from 'lucide-react';
import { ActionButtonBase } from '../../common/ActionButtonBase';
import { TabHeader } from '../../common/TabHeader';
import { PillToggleGroup } from '../../common/PillToggleGroup';
import { useIsMobile } from '../../../hooks/useIsMobile';
import type { TranslationKeys } from '../../../i18n/fr';

export type PositioningMode = 'chat' | 'text' | 'history' | 'brandConfig';

interface PositioningHeaderProps {
  mode: PositioningMode;
  setMode: (mode: PositioningMode) => void;
  onClose: () => void;
  onboardingActive?: boolean;
  forceStartOnboarding?: () => void;
  t: TranslationKeys;
}

/**
 * En-tête du positionnement avec toggle de mode (chat / texte / charte graphique).
 *
 * Refonte 2026-05 (chantiers O + P) :
 *  - **Style du bouton actif** : icône blanche sur fond violet primary (au lieu d'un
 *    surface-on-surface-hover quasi invisible). L'utilisateur comprend immédiatement
 *    dans quel mode il se trouve.
 *  - **Tooltips Radix** : remplacent les `title="..."` natifs HTML (lents à apparaître,
 *    sans styling). Cohérence avec le reste du repo (SourceListItem, ChannelForm).
 *  - **3e bouton "Charte graphique"** (chantier P) : icône Palette. Bascule sur la
 *    vue brand config inline (palette + fonts + images extraites de la landing page).
 */
export function PositioningHeader({
  mode,
  setMode,
  onClose,
  onboardingActive,
  forceStartOnboarding,
  t,
}: PositioningHeaderProps) {
  const isMobile = useIsMobile();

  // Restart onboarding button — visible when chat mode and not in onboarding
  const restartButton = mode === 'chat' && !onboardingActive && forceStartOnboarding && (
    <ActionButtonBase
      icon={RefreshCw}
      label={isMobile ? undefined : (t.onboarding?.restartOnboarding || 'Recommencer l\'assistant')}
      onClick={forceStartOnboarding}
      variant="default"
      appearance="outline"
      revealLabelOnHover={true}
      className="h-8"
    />
  );

  // Mode Toggle — 3 boutons : Chat / Texte / Charte graphique.
  // UX backlog M.5 : sur mobile, on affiche le label à côté de l'icône
  // (alwaysShowLabel={isMobile}) ; sur desktop, icon-only + Radix Tooltip.
  // Note : modeBrandConfigShort ('Charte') remplacé par modeBrandConfig
  // ('Charte graphique') — label unique pour tooltip desktop et label mobile.
  const modeToggle = (
    <PillToggleGroup
      items={[
        { id: 'chat', label: t.positioning?.modeChat || 'Agent', icon: MessageCircle },
        { id: 'text', label: t.positioning?.modeText || 'Texte', icon: AlignLeft },
        { id: 'brandConfig', label: t.positioning?.modeBrandConfig || 'Charte graphique', icon: Palette },
      ]}
      value={mode}
      onChange={(id) => setMode(id as PositioningMode)}
      ariaLabel={t.positioning?.modeToggleLabel || 'Mode'}
      size={isMobile ? 'sm' : 'md'}
      alwaysShowLabel={isMobile}
    />
  );

  return (
    // Coque partagée V4-FRONT-HEADERS-UNIFORM (référence Engagement)
    <TabHeader
      title={t.positioning?.title || 'Positionnement'}
      onClose={onClose}
      // V4-MOBILE-POSITIONING-TOOLBAR-OVERLAP-NAV / V4-MOBILE-POSITIONING-TABS-CLIPPED
      // (2026-07-03) — sur mobile, restartButton + les 3 pills en labels complets
      // ("Charte graphique" ≈ 100px) ne tenaient plus dans la ligne du titre :
      // le bouton fermer (X) était repoussé hors du viewport (vérifié : x=404-440
      // pour un viewport de 390px, donc totalement hors-écran et non cliquable).
      // Faute de pouvoir fermer ni voir le burger de nav (masqué dessous, z-index
      // plus bas), l'utilisateur restait bloqué sur l'onglet. Fix : sur mobile, le
      // toggle de mode (+ restart) passe sur une 2e ligne pleine largeur (children),
      // ne laissant que le X dans la ligne du titre.
      actions={!isMobile && (
        <>
          {restartButton}
          {modeToggle}
        </>
      )}
    >
      {isMobile && (
        <div className="flex items-center gap-2 flex-wrap mt-3">
          {restartButton}
          {modeToggle}
        </div>
      )}
    </TabHeader>
  );
}
